import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import {
  Favourites,
  FlightRequestByIdResponse,
  MyRequestItem,
  MyRequestResponse,
} from "./types";
import {
  addFavourites,
  deleteFavourites,
  deleteLoadRequests,
  enableUpdateRequests,
  getFavourites,
  getLoadRequestDetailsByID,
  getRequestUpdates,
  pinUnpinRequests,
  validateFavouriteFlight,
} from "apis/myRequestsService";
import { useCreditStore } from "store/creditStore";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  getAirlineNotesById,
  AirlineNotesResponse,
} from "apis/airlineNotesService";
import { getAirportOffsetLabel } from "utils/airportTimezones";
import RequestUpdateModal from "./components/RequestUpdateModal";
import DeleteRequestModal from "./components/DeleteRequestModal";
import { useToastStore } from "store/toastStore";
import ShareRequestModal from "./components/ShareRequestModal";
import ReportInaccurateLoadPopup from "./components/ReportInaccurateLoadPopup";
import DefaultAirLogo from "../../assets/images/logo.svg";
import AirlineNotesModal from "components/AirlineNotesModal";
import { AirlineNote, AirlineNoteSections } from "types/airlineNotes";
import { Loader2 } from "lucide-react";
import { useGlobalLoader } from "../../../src/contexts/GlobalLoaderContext";
import { useClickOutside } from "../../hooks/useClickOutside";
import AnsweredFlightDetails from "./components/AnsweredFlightDetails";

// Assuming this is where you're exporting the API function

interface Flight {
  id: number;
  isPinned?: boolean; // Optional since bulk updates may not require this
}

interface FlightListingProps {
  flights: MyRequestResponse;
  action: string | null;
  // Pass silent=true for a background refresh that does NOT show the global
  // loader (used to refill/reconcile the page after an optimistic delete).
  fetchLoadRequests: (silent?: boolean) => void;
  isFavouritesPage?: boolean;
  loading?: boolean; // show loader while requests are being fetched
  loaderState?: boolean;
  // Rendered inline right after the list, below the flight cards (favourites
  // page only) — the fixed bottom bar holds just the POST REQUEST button.
  footer?: React.ReactNode;
  // Reports the pin state of the current selection to the parent so the toolbar
  // "Actions" menu can show Pin vs Unpin. true = all selected pinned, false =
  // not all pinned, null = nothing selected.
  onSelectionChange?: (pinned: boolean | null) => void;
  selectedRequestId?: number | string | null;
}

const transformNotesResponse = (
  data: AirlineNotesResponse,
  fallbackName: string,
): AirlineNote => {
  const sections: AirlineNoteSections = {};

  if (data.notes) {
    Object.entries(data.notes).forEach(([key, rawValue]) => {
      if (rawValue === null || rawValue === undefined) return;

      if (Array.isArray(rawValue)) {
        const cleanedItems = rawValue
          .map((item) => (typeof item === "string" ? item.trim() : ""))
          .filter((item): item is string => item.length > 0);

        if (cleanedItems.length > 0) {
          sections[key] = cleanedItems;
        }
        return;
      }

      if (typeof rawValue === "string") {
        const trimmed = rawValue.trim();
        if (trimmed.length > 0) {
          sections[key] = trimmed;
        }
      }
    });
  }

  return {
    id: data.id,
    airlineId: data.airlineId,
    airlineName: data.airlineName || fallbackName,
    sections,
    lastUpdated: data.createdAt,
    createdAt: data.createdAt,
  };
};

// Per-status row accent: the card is white and carries a status-coloured
// outline plus a thicker left bar (red = Pending/Expired, green = Answered),
// so the state of a request reads at a glance without a filled pill.
const statusAccent = (
  status?: string,
): { card: string; bar: string; text: string } => {
  // Match case-insensitively so backend casing variants land on the right
  // colour instead of falling through to the neutral grey default.
  switch ((status || "").trim().toLowerCase()) {
    case "answered":
    case "Updated Answer":
      return {
        card: "border-[#7DD3A8]",
        bar: "border-l-[#22A06B]",
        text: "text-[#16A34A]",
      };
    case "pending":
      return {
        card: "border-[#F4A3A5]",
        bar: "border-l-[#DC4043]",
        text: "text-[#DC4043]",
      };
    case "expired":
      return {
        card: "border-[#737C8C]/40",
        bar: "border-l-[#737C8C]/40",
        text: "text-[#DC4043]",
      };
    case "request updating":
    case "update requested":
    case "updated requested":
      return {
        card: "border-[#FBD38D]",
        bar: "border-l-[#F59E0B]",
        text: "text-[#C2410C]",
      };
    // Answer refreshed after an update request: blue, so it never reads as
    // Expired grey beside the other states.
    case "updated answer":
    case "answer updated":
      return {
        card: "border-[#93C5FD]",
        bar: "border-l-[#2563EB]",
        text: "text-[#1D4ED8]",
      };
    default:
      return {
        card: "border-[#EDEEF1]",
        bar: "border-l-[#C5CAD4]",
        text: "text-[#6B7280]",
      };
  }
};

// Statuses whose row already carries a submitted answer, so the answer
// accordion (responder, open seats, live status) must render. "Updated Answer"
// rows carry the LATEST LoadResponse values in the same fields as "Answered",
// so they use the same body.
const hasAnswerDetails = (status?: string): boolean => {
  switch ((status || "").trim().toLowerCase()) {
    case "answered":
    case "update requested":
    case "updated requested":
    case "request updating":
    case "updated answer":
    case "answer updated":
      return true;
    default:
      return false;
  }
};

// True only for rows whose answer is a refreshed one, used for the "Updated"
// marker and the "Updated … ago" wording.
const isUpdatedAnswer = (status?: string): boolean => {
  const key = (status || "").trim().toLowerCase();
  return key === "updated answer" || key === "answer updated";
};

// True only for rows with a settled answer that hasn't already had an update
// requested. hasAnswerDetails() also covers "update requested"/"request
// updating" (so the answer body keeps rendering while it's pending), but
// reusing that same check to gate the "Request Update" action let users fire
// it again on a row that already had a pending update — this is the
// dedicated, narrower check for that action.
const canRequestUpdate = (status?: string): boolean => {
  const key = (status || "").trim().toLowerCase();
  return key === "answered" || key === "updated answer" || key === "answer updated";
};

// Same narrowing as canRequestUpdate: a row with an update already pending
// ("update requested"/"request updating") has nothing new to report as
// inaccurate until that update comes back.
const canReportInaccurate = (status?: string): boolean => {
  const key = (status || "").trim().toLowerCase();
  return key === "answered" || key === "updated answer" || key === "answer updated";
};

const FlightListing: React.FC<FlightListingProps> = ({
  flights,
  action,
  fetchLoadRequests,
  isFavouritesPage,
  loading,
  footer,
  onSelectionChange,
  selectedRequestId,
}) => {
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  // Tracks favourite requests already in flight so rapid clicks on the same
  // row don't fire overlapping add/delete calls (which caused "Error updating
  // favourites" because the favouriteId wasn't backfilled yet).
  const pendingFavouriteIds = useRef<Set<any>>(new Set());

  const [actionIsOpen, setActionIsOpen] = useState(false);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [openAccordion, setOpenAccordion] = useState<Set<number>>(new Set());
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  // Flips the row action menu above its trigger when there isn't enough
  // room below (e.g. the last row in the list), so it doesn't run off-screen.
  const [dropdownPosition, setDropdownPosition] = useState<"down" | "up">(
    "down"
  );
  const [flightData, setFlightData] =
    useState<FlightRequestByIdResponse | null>(null);
  const [isArrival, setIsArrival] = useState(false);
  const [selectedFlights, setSelectedFlights] = useState<string[]>([]);
  const [isRequestUpdateOpen, setIsRequestUpdateOpen] = useState(false);
  const [currentFlight, setCurrentFlight] = useState<any>(null);
  const [isBulkOperation, setIsBulkOperation] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState<boolean>(false);
  const [selectedFlightId, setSelectedFlightId] = useState<number | null>(null);
  const [isShareRequestModalOpen, setShareRequestModalOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState(""); // Store generated URL
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [selectedAirlineNote, setSelectedAirlineNote] =
    useState<AirlineNote | null>(null);
  const [notesCache, setNotesCache] = useState<Record<number, AirlineNote>>({});
  const [notesLoadingFlightId, setNotesLoadingFlightId] = useState<
    number | null
  >(null);
  const [favourites, setFavourites] = useState<Favourites[]>([]);
  const [localFlights, setLocalFlights] = useState<MyRequestItem[]>([]);
  // Favourites-page-only state (the POST-favourite flow)
  const [isPostModalOpen, setIsPostModalOpen] = useState(false);
  const [postDate, setPostDate] = useState<string>(() =>
    new Date().toISOString().slice(0, 10),
  );
  const [postRequestLoader, setPostRequestLoader] = useState<boolean>(false);
  const { showLoader, hideLoader } = useGlobalLoader();

  // Local mirror of the list so pin / favourite actions can update the UI
  // optimistically (instant feedback) while the API call runs in the
  // background. Re-syncs whenever the parent supplies fresh data (pagination,
  // filters, refetch after delete, etc.), so the component stays reusable and
  // the parent doesn't need to change.
  useEffect(() => {
    setLocalFlights(flights?.items ?? []);
  }, [flights?.items]);

  // Tokens-per-request-update, from the same admin-configured credit config
  // the Loads search results banner reads. Fetched here too since a user can
  // land on Your Requests without ever visiting Loads first.
  const requestCost = useCreditStore((state) => state.requestCost);
  useEffect(() => {
    useCreditStore.getState().refreshRequestCost();
  }, []);

  // Selection now persists across pagination, but `localFlights` only ever
  // holds the CURRENTLY LOADED page — bulk Pin/Unpin and Delete need each
  // row's isPinned/requestDetailId, not just its id. Cache the full row for
  // every currently-selected id as pages are visited, so a selection made on
  // page 1 still has its data available once the user has moved on to page 2
  // and triggers a bulk action from there.
  const [selectedFlightDetails, setSelectedFlightDetails] = useState<
    Record<string, any>
  >({});
  useEffect(() => {
    setSelectedFlightDetails((prev) => {
      const next: Record<string, any> = {};
      selectedFlights.forEach((id) => {
        const onThisPage = localFlights.find(
          (f: any) => f.id.toString() === id,
        );
        if (onThisPage) next[id] = onThisPage;
        else if (prev[id]) next[id] = prev[id];
      });
      return next;
    });
  }, [selectedFlights, localFlights]);

  useEffect(() => {
    if (loading) {
      showLoader();
    } else {
      hideLoader();
    }
  }, []);

  // Report the selection's pin state to the parent so the toolbar Actions menu
  // can show "Pin" vs "Unpin". All selected already pinned → report true.
  useEffect(() => {
    if (!onSelectionChange) return;
    if (selectedFlights.length === 0) {
      onSelectionChange(null);
      return;
    }
    const selected = Object.values(selectedFlightDetails);
    const allPinned =
      selected.length > 0 && selected.every((f: any) => f.isPinned);
    onSelectionChange(allPinned);
  }, [selectedFlights, selectedFlightDetails, onSelectionChange]);

  useEffect(() => {
    const initialOpen = new Set<number>();
    flights?.items?.forEach((flight: any) => {
      if (hasAnswerDetails(flight?.status)) {
        initialOpen.add(flight.id);
      }
    });
    setOpenAccordion(initialOpen);
  }, [flights]);

  const hasAutoSelectedTargetRef = useRef(false);
  const prevSelectedRequestIdRef = useRef(selectedRequestId);

  // When arriving from recently posted requests with a selected request ID,
  // select it, ensure its details are open, and smoothly scroll it into view ONCE.
  useEffect(() => {
    // A different selectedRequestId (e.g. a second notification clicked while
    // this page is already mounted, so no fresh component/ref instance is
    // created) needs to re-run the highlight below rather than being skipped
    // by the "ONCE" guard from the previous target.
    if (selectedRequestId !== prevSelectedRequestIdRef.current) {
      hasAutoSelectedTargetRef.current = false;
      prevSelectedRequestIdRef.current = selectedRequestId;
    }
    if (!selectedRequestId || hasAutoSelectedTargetRef.current) return;
    const reqIdStr = selectedRequestId.toString();
    const exists = localFlights.some((f: any) => f.id.toString() === reqIdStr);
    if (exists) {
      hasAutoSelectedTargetRef.current = true;
      setSelectedFlights([reqIdStr]);
      setOpenAccordion((prev) => {
        const next = new Set(prev);
        next.add(Number(selectedRequestId));
        return next;
      });
      // Poll for the row instead of a single fixed-delay attempt: a loading
      // flicker (e.g. a background refetch) can unmount the row right around
      // the 200ms mark, and a one-shot lookup would then silently no-op with
      // no retry, leaving the page highlighted-but-unscrolled.
      let attempts = 0;
      const maxAttempts = 20;
      const tryScroll = () => {
        const el = document.getElementById(
          `accordion-collapse-${selectedRequestId}`,
        );
        if (el) {
          // scrollIntoView walks every scrollable ancestor (the <main>
          // app-shell-scroll container here) and centers the row, which is
          // more reliable than hand-computing an offset against a single
          // container.
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          return;
        }
        attempts += 1;
        if (attempts < maxAttempts) {
          setTimeout(tryScroll, 150);
        }
      };
      setTimeout(tryScroll, 200);
    }
  }, [selectedRequestId, localFlights]);

  // Multi-select: toggles a single flight in/out of the selection, leaving
  // every other currently-selected flight (including ones on other pages)
  // untouched.
  const handleSelectFlight = (flightId: string) => {
    setSelectedFlights((prev) =>
      prev.includes(flightId)
        ? prev.filter((id) => id !== flightId)
        : [...prev, flightId],
    );
  };

  // --- Favourites-page-only helpers ---
  // On the favourites page each row IS a favourite, so removing it deletes the
  // favourite by its id (mirrors the old FavouritesListing behaviour).
  const handleRemoveFavourite = async (flight: any) => {
    // Ignore rapid repeat clicks while a request for this row is in flight.
    if (pendingFavouriteIds.current.has(flight.id)) return;
    pendingFavouriteIds.current.add(flight.id);
    // Optimistically drop the row, then confirm with the backend. Restore it if
    // the call fails.
    const snapshot = localFlights;
    setLocalFlights((prev) => prev.filter((f: any) => f.id !== flight.id));
    try {
      await deleteFavourites(flight.id);
      // Silent refresh to refill the page and reconcile the pagination count
      // (no global loader — the row already vanished optimistically).
      fetchLoadRequests(true);
      useToastStore
        .getState()
        .showToast("Removed from favourites successfully", "success", 3000);
    } catch (error) {
      setLocalFlights(snapshot);
      useToastStore
        .getState()
        .showToast("Error updating favourites", "error", 3000);
    } finally {
      pendingFavouriteIds.current.delete(flight.id);
    }
  };

  const openPostModal = () => {
    if (selectedFlights.length === 0) {
      useToastStore
        .getState()
        .showToast("Please select at least one flight.", "error", 3000);
      return;
    }
    setIsPostModalOpen(true);
  };

  const toggleFavourite = async (flight: any) => {
    // Ignore rapid repeat clicks while a request for this row is in flight.
    if (pendingFavouriteIds.current.has(flight.id)) return;
    pendingFavouriteIds.current.add(flight.id);
    const snapshot = localFlights;
    const adding = !flight.isFavourite;

    // Flip the heart immediately for instant feedback.
    setLocalFlights((prev) =>
      prev.map((f: any) =>
        f.id === flight.id
          ? {
              ...f,
              isFavourite: adding,
              favouriteId: adding ? f.favouriteId : null,
            }
          : f,
      ),
    );

    try {
      if (adding) {
        const favouritesData = {
          airlineId: flight.airlineId,
          flightNumber: flight.flightNo,
          sourceCode: flight.sourceCode,
          destinationCode: flight.destinationCode,
          deptim: flight.deptim,
          arrtim: flight.arrtim,
          depterm: flight.departureTerminal,
          arrterm: flight.arrivalTerminal,
          elptim: flight.elptim,
          depcity: flight.depcity,
          arrcity: flight.arrcity,
          duration: flight.duration,
          originalDate: flight.departure,
          label: "Favourites",
        };

        const res = await addFavourites(favouritesData);
        // Backfill the new favourite id so an immediate un-favourite works
        // without needing a full refetch.
        const created = res?.data ?? res;
        const newFavouriteId = created?.favouriteId ?? created?.id ?? null;
        setLocalFlights((prev) =>
          prev.map((f: any) =>
            f.id === flight.id
              ? { ...f, isFavourite: true, favouriteId: newFavouriteId }
              : f,
          ),
        );
        useToastStore
          .getState()
          .showToast("Added to favourites successfully", "success", 3000);
      } else {
        await deleteFavourites(flight.favouriteId);
        useToastStore
          .getState()
          .showToast("Removed from favourites successfully", "success", 3000);
      }
    } catch (error) {
      // ❌ rollback on failure
      setLocalFlights(snapshot);
      useToastStore
        .getState()
        .showToast("Error updating favourites", "error", 3000);
    } finally {
      pendingFavouriteIds.current.delete(flight.id);
    }
  };

  // Convert selected flight IDs to numbers for the API
  const getSelectedRequestIds = (): number[] => {
    return selectedFlights
      .map((id) => parseInt(id, 10))
      .filter((id) => !isNaN(id));
  };

  // Selection persists across pagination (Task 14), so it must NOT be pruned
  // just because a selected id isn't on the currently-loaded page. Only clear
  // it when there are truly no requests left at all (e.g. after deleting
  // everything, or a filter that matches nothing).
  useEffect(() => {
    if ((flights?.totalCount ?? 0) === 0) {
      setSelectedFlights([]);
    }
  }, [flights?.totalCount]);

  useEffect(() => {
    if (action && selectedFlights.length === 0) {
      useToastStore
        .getState()
        .showToast("No Requests selected.", "error", 3000);
      return; // Stop execution
    }

    setIsBulkOperation(true);
    setCurrentFlight(null);

    if (action === "requestUpdate") {
      setIsRequestUpdateOpen(true);
    } else if (action === "deleteRequest") {
      setIsDeleteModalOpen(true);
    } else if (action === "enableStatusUpdate") {
      const numericFlightIds = selectedFlights.map((id) => Number(id)); // Convert to numbers
      const handleStatusUpdate = async () => {
        try {
          const response = await enableUpdateRequests(numericFlightIds); // Pass converted array
          useToastStore
            .getState()
            .showToast("Status Update successful", "success", 3000);
        } catch (error) {
          useToastStore
            .getState()
            .showToast("Error updating status", "error", 3000);
        }
      };

      handleStatusUpdate(); // Call the async function
    } else if (action === "pinFlight") {
      // Toggle: if every selected flight is already pinned, unpin them;
      // otherwise pin. This makes the bulk action mirror the per-row menu.
      // Uses the cached selectedFlightDetails (not localFlights) since the
      // selection can span pages beyond the one currently loaded.
      const selected = Object.values(selectedFlightDetails);
      const allPinned =
        selected.length > 0 && selected.every((f: any) => f.isPinned);
      const mappedFlights: Flight[] = selected.map((f: any) => ({
        id: Number(f.id),
        isPinned: f.isPinned,
      }));

      handlePinUnPin(mappedFlights, true, !allPinned);
    }
  }, [action]);

  // Open single flight update modal
  const handleSingleUpdate = (flight: any) => {
    setIsBulkOperation(false);
    setCurrentFlight(flight);
    setOpenDropdown(null); // close the row menu as the confirm dialog opens
    setIsRequestUpdateOpen(true);
  };

  // Optimistic request-update: close the dialog, flip the row status and deduct
  // the credit immediately, run the API in the background, then silently
  // reconcile (status + credit) — rolling back on failure.
  const handleConfirmRequestUpdate = async () => {
    const targetIds = isBulkOperation
      ? getSelectedRequestIds()
      : currentFlight
        ? [currentFlight.id]
        : [];

    if (targetIds.length === 0) {
      setIsRequestUpdateOpen(false);
      return;
    }

    const snapshot = localFlights;

    // UI updates first.
    setIsRequestUpdateOpen(false);
    setOpenDropdown(null);
    setLocalFlights((prev) =>
      prev.map((f: any) =>
        targetIds.includes(f.id) ? { ...f, status: "Update Requested" } : f,
      ),
    );
    useCreditStore.getState().decrement(targetIds.length * (requestCost ?? 1));

    try {
      await getRequestUpdates(targetIds);
      if (isBulkOperation) setSelectedFlights([]);
      useToastStore
        .getState()
        .showToast("Request updated successfully", "success", 3000);
      // Reconcile authoritative status + credit balance in the background.
      fetchLoadRequests(true);
      useCreditStore.getState().refresh();
    } catch (error) {
      setLocalFlights(snapshot); // rollback status
      useCreditStore.getState().refresh(); // restore the real balance
      useToastStore
        .getState()
        .showToast(
          "Failed to request update. Please try again.",
          "error",
          3000,
        );
    }
  };

  const handleSingleDelete = (flight: any) => {
    setIsBulkOperation(false);
    setCurrentFlight(flight);
    setOpenDropdown(null); // close the row menu as the confirm dialog opens
    setIsDeleteModalOpen(true);
  };

  // Optimistic delete: close the dialog and drop the row(s) immediately, then
  // run the API in the background and roll back if it fails. Handles both the
  // single (row menu) and bulk (action menu) cases.
  const handleConfirmDelete = async () => {
    // On the favourites page a row is a favourite (no load-request behind it),
    // so "delete" means remove the favourite. Reuse the optimistic favourite
    // removal instead of the load-request delete API.
    if (isFavouritesPage) {
      setIsDeleteModalOpen(false);
      setOpenDropdown(null);
      if (currentFlight) await handleRemoveFavourite(currentFlight);
      return;
    }

    // Uses the cached selectedFlightDetails (not localFlights) since the
    // selection can span pages beyond the one currently loaded.
    const targets = isBulkOperation
      ? Object.values(selectedFlightDetails)
      : currentFlight
        ? [currentFlight]
        : [];

    const requestDetailIds = targets
      .map((f: any) => f.requestDetailId)
      .filter(Boolean);

    if (requestDetailIds.length === 0) {
      setIsDeleteModalOpen(false);
      return;
    }

    const removedIds = targets.map((f: any) => f.id);
    const snapshot = localFlights;

    // UI updates first.
    setIsDeleteModalOpen(false);
    setOpenDropdown(null);
    setLocalFlights((prev) =>
      prev.filter((f: any) => !removedIds.includes(f.id)),
    );
    if (isBulkOperation) setSelectedFlights([]);

    try {
      await deleteLoadRequests(requestDetailIds);
      // Silent refresh to refill the page and reconcile the pagination count
      // (no global loader — the row already vanished optimistically).
      fetchLoadRequests(true);
      useToastStore
        .getState()
        .showToast("Request deleted successfully", "success", 3000);
    } catch (error) {
      setLocalFlights(snapshot); // restore on failure
      useToastStore
        .getState()
        .showToast("Error deleting request", "error", 3000);
    }
  };

  const handleSingleStatusUpdate = async (flight: any) => {
    try {
      const response = await enableUpdateRequests([flight?.id]); // Pass as an array
      useToastStore
        .getState()
        .showToast("Status Update successful", "success", 3000);
      fetchLoadRequests();
    } catch (error) {
      useToastStore
        .getState()
        .showToast("Error updating status", "error", 3000);
    }
  };

  const handlePinUnPin = async (
    flights: Flight | Flight[],
    isBulk = false,
    // Explicit target state for bulk toggles (pin vs unpin). When omitted, a
    // single-flight action flips its own current state.
    nextPinnedOverride?: boolean,
  ): Promise<void> => {
    const requestIds = isBulk
      ? (flights as Flight[]).map((flight) => flight.id)
      : [(flights as Flight).id];

    const nextPinned =
      nextPinnedOverride !== undefined
        ? nextPinnedOverride
        : isBulk
          ? true
          : !(flights as Flight).isPinned;

    // Close the row menu right away (single-flight action from the dropdown).
    if (!isBulk) setOpenDropdown(null);

    // Optimistically reflect the new pin state and move pinned requests to the top.
    const snapshot = localFlights;
    setLocalFlights((prev) => {
      const updated = prev.map((f: any) =>
        requestIds.includes(f.id) ? { ...f, isPinned: nextPinned } : f,
      );
      return [...updated].sort((a: any, b: any) => {
        if (Boolean(a.isPinned) === Boolean(b.isPinned)) return 0;
        return a.isPinned ? -1 : 1;
      });
    });

    try {
      await pinUnpinRequests(requestIds, nextPinned);
      const message = nextPinned
        ? "Pinned successfully"
        : "Unpinned successfully";
      useToastStore.getState().showToast(message, "success", 3000);
      fetchLoadRequests(true);
    } catch (error) {
      console.error("Error in pin/unpin:", error);
      setLocalFlights(snapshot);
      useToastStore
        .getState()
        .showToast("Failed to update status", "error", 3000);
    }
  };

  const toggleDropdown = (id: number): void => {
    setOpenDropdown(openDropdown === id ? null : id);
    setDropdownPosition("down");
  };

  // Measure the open row menu after it renders and flip it above the trigger
  // if it would otherwise overflow the bottom of the viewport.
  useLayoutEffect(() => {
    if (openDropdown === null) return;
    const el = dropdownRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.bottom > window.innerHeight) {
      setDropdownPosition("up");
    }
  }, [openDropdown]);

  const handleOpenNotes = async (
    flight: MyRequestItem,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();

    const airlineId = flight?.airlineId;
    const flightId = flight?.id;

    if (!airlineId) {
      useToastStore
        .getState()
        .showToast(
          "Airline information not available for this request.",
          "error",
          3000,
        );
      return;
    }

    const cached = notesCache[airlineId];
    if (cached) {
      setSelectedAirlineNote(cached);
      setIsNotesModalOpen(true);
      return;
    }

    try {
      setNotesLoadingFlightId(flightId ?? null);
      const response = await getAirlineNotesById(airlineId);
      const transformed = transformNotesResponse(
        response,
        flight?.airlineName || "Unknown",
      );
      setNotesCache((prev) => ({ ...prev, [airlineId]: transformed }));
      setSelectedAirlineNote(transformed);
      setIsNotesModalOpen(true);
    } catch (error) {
      console.error("Error fetching airline notes:", error);
      useToastStore
        .getState()
        .showToast(
          "This airline has no custom notes available yet",
          "error",
          3000,
        );
    } finally {
      setNotesLoadingFlightId(null);
    }
  };

  const handleCloseNotes = () => {
    setIsNotesModalOpen(false);
    setSelectedAirlineNote(null);
  };

  useClickOutside(dropdownRef, () => setOpenDropdown(null));
  return (
    <section className="mt-1">
      {/* Selection count — hidden on the favourites page */}
      {!isFavouritesPage && selectedFlights.length > 0 && (
        <div className="flex items-center justify-end mb-2 mx-3 sm:mx-4">
          <p className="text-sm text-[#6B7280]">
            {selectedFlights.length} selected
          </p>
        </div>
      )}

      {loading ? (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-md flex items-center justify-center z-50 animate-in fade-in duration-200"
          style={{ zIndex: 9999 }} // Force top layer
        >
          <div className="bg-white/90 backdrop-blur rounded-xl p-8 shadow-2xl flex flex-col items-center gap-4 animate-in zoom-in-50 duration-200">
            <Loader2 className="w-12 h-12 text-[#FFD700] animate-spin" />
            <p className="text-gray-800 font-semibold text-lg">Loading</p>
          </div>
        </div>
      ) : localFlights.length === 0 ? (
        <div className="flex justify-center items-center h-32 w-full text-gray-500 mt-8">
          <p>
            {isFavouritesPage
              ? "No favourites found"
              : "No flight requests found."}
          </p>
        </div>
      ) : (
        localFlights.map((flight: any) => (
          <div
            key={flight.id}
            className="mb-2"
            id={`accordion-collapse-${flight.id}`}
            data-accordion="collapse"
          >
            <div
              onClick={() => handleSelectFlight(flight.id.toString())}
              className={`bg-[#F3F4F6] rounded-md pl-2 pr-3 sm:px-4 py-2.5 sm:py-3 w-full border-2 relative cursor-pointer transition-all duration-200 ease-out hover:shadow-md hover:-translate-y-0.5 ${
                isFavouritesPage ? "" : "border-l-4"
              } ${
                selectedFlights.includes(flight.id.toString())
                  ? "border-[#FFBC00] border-l-[#FFBC00] ring-1 ring-[#FFBC00] shadow-md"
                  : isFavouritesPage
                    ? "border-[#EDEEF1]"
                    : `${statusAccent(flight?.status).card} ${statusAccent(flight?.status).bar}`
              } ${openDropdown === flight.id ? "z-40" : ""}`}
            >
              <div className="flex items-center min-w-0">
                {/* Multi-select checkbox. onClick stops propagation so it
                    doesn't double-toggle via the card's own onClick handler. */}
                <input
                  type="checkbox"
                  checked={selectedFlights.includes(flight.id.toString())}
                  onChange={() => handleSelectFlight(flight.id.toString())}
                  onClick={(e) => e.stopPropagation()}
                  className="mr-1 sm:mr-4 w-4 h-4 cursor-pointer flex-shrink-0 rounded border-gray-300 text-[#FFBC00] accent-[#FFBC00] focus:outline-none focus:ring-2 focus:ring-[#FFBC00]"
                />

                <div
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    // The whole card selects (handled by the container's
                    // onClick). The answered-details accordion auto-opens via
                    // its effect, so this inner element just lets the click
                    // bubble up to select.
                    e.stopPropagation();
                    handleSelectFlight(flight.id.toString());
                  }}
                  // Suppress the browser's default focus outline (Safari/Firefox
                  // and touch keep this blue box focused/visible even after
                  // deselecting). Keep a keyboard-only gold ring for a11y.
                  className="w-full min-w-0 text-left rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFBC00] focus-visible:ring-inset"
                  aria-expanded={openAccordion.has(flight?.id)}
                >
                  {/* <sm the row wraps into stacked blocks (airline / date +
                      status / route) so nothing is squeezed on a phone; from
                      sm up it stays the original single nowrap row. */}
                  <div className="flex flex-wrap sm:flex-nowrap items-start sm:items-center gap-y-3 gap-x-3 font-manrope">
                    {/* Airline Logo + Name + Flight No */}
                    {/* pr on mobile reserves space for the action buttons, which
                        are pulled to the card's top-right corner below <sm. */}
                    <div className="flex items-center min-w-0 basis-full sm:basis-[18%] sm:shrink-0 gap-2 sm:gap-3 pr-28 sm:pr-0">
                      <img
                        className="w-12 h-12 rounded-lg object-contain flex-shrink-0 border border-[#EDEEF1]"
                        src={flight.airlineLogo || DefaultAirLogo}
                        alt={"Airline"}
                        onError={(e) =>
                          (e.currentTarget.src = "/assets/logos/air-logo.png")
                        }
                      />
                      <div className="text-left min-w-0">
                        <p className="text-[#1F2937] text-sm font-semibold leading-tight">
                          {flight?.airlineName}
                        </p>
                        <div className="flex items-center gap-1.5 min-w-0 mt-0.5">
                          <p className="text-[#6B7280] text-xs">
                            {flight?.flightNo}
                          </p>
                          <button
                            onClick={(e) => handleOpenNotes(flight, e)}
                            className={`h-[23px] px-2 rounded-md transition group border border-[#ECEEF1] bg-white flex items-center gap-1 flex-shrink-0 whitespace-nowrap ${
                              notesLoadingFlightId === flight?.id
                                ? "bg-yellow-50 cursor-not-allowed"
                                : "hover:bg-yellow-50"
                            }`}
                            aria-label="View airline notes"
                            title="View airline notes"
                            disabled={notesLoadingFlightId === flight?.id}
                          >
                            {notesLoadingFlightId === flight?.id ? (
                              <svg
                                className="w-3 h-3 text-[#FFBC00] animate-spin"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                />
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                />
                              </svg>
                            ) : (
                              <i className="st-info text-[#FFBC00] text-[10px] group-hover:text-yellow-600"></i>
                            )}
                            <span className="text-[#262626] text-[11px] font-medium">
                              Airline Note
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Date */}
                    <div className="min-w-0 basis-[46%] sm:basis-[14%] sm:shrink-0">
                      <p className="text-[#6B7280] text-xs text-left">Date</p>
                      <p className="text-[#1F2937] mt-1 text-sm font-medium text-left whitespace-nowrap">
                        {flight?.departure
                          ? (() => {
                              const date = new Date(flight.departure);
                              const month = date.toLocaleDateString("en-US", {
                                month: "long",
                                timeZone: "UTC",
                              });
                              const day = String(date.getUTCDate()).padStart(
                                2,
                                "0",
                              );
                              const year = date.getUTCFullYear();
                              return `${day} ${month}, ${year}`;
                            })()
                          : ""}
                      </p>
                    </div>

                    {/* Status */}
                    {!isFavouritesPage && (
                      <div className="flex flex-col items-start min-w-0 basis-[46%] sm:basis-[10%] sm:shrink-0">
                        <p className="text-[#6B7280] text-xs">Status</p>
                        <p
                          className={`text-sm font-semibold mt-1 w-fit whitespace-nowrap ${statusAccent(flight?.status).text}`}
                        >
                          {flight?.status}
                        </p>
                        {flight?.isEnabledStatus === true &&
                          hasAnswerDetails(flight?.status) && (
                            <p
                              className="text-[10px] font-medium mt-1 px-2 py-0.5 rounded-md w-fit whitespace-nowrap bg-blue-100 text-blue-600 flex items-center gap-1"
                              title="You've enabled status updates for this flight"
                            >
                              <i className="st-warning text-[10px]" />
                              Status Update Enabled
                            </p>
                          )}
                      </div>
                    )}

                    {/* Route section */}
                    <div className="flex items-center min-w-0 basis-full sm:basis-auto sm:flex-1 gap-1 sm:gap-3">
                      <div className="flex flex-col items-start gap-0.5 w-[44px] sm:w-[88px] flex-shrink-0">
                        <p className="text-[#1F2937] text-base font-bold">
                          {flight?.sourceCode}
                        </p>
                        <div className="flex items-start gap-1">
                          <i className="st-top-right text-[#6B7280] text-[9px] leading-none flex items-center mt-[1px]"></i>
                          <p className="text-[#6B7280] text-[11px] leading-none">
                            {flight?.departure
                              ? new Date(flight.departure).toLocaleTimeString(
                                  "en-GB",
                                  {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: false,
                                    timeZone: "UTC",
                                  },
                                )
                              : ""}
                            {flight?.departure &&
                              getAirportOffsetLabel(
                                flight?.sourceCode,
                                flight.departure,
                              ) && (
                                <span className="text-[#9CA3AF] hidden sm:inline ml-1 whitespace-nowrap">
                                  (
                                  {getAirportOffsetLabel(
                                    flight?.sourceCode,
                                    flight.departure,
                                  )}
                                  )
                                </span>
                              )}
                          </p>
                        </div>
                      </div>

                      <div className="flex-1 min-w-[22px] sm:min-w-[20px]">
                        <div className="flex items-center">
                          <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#C5CAD4] rounded-full flex-shrink-0"></div>
                          <div className="flex-1 border-b border-dashed border-[#9CA3AF] mx-1 min-w-[8px]"></div>
                          <i className="st-flight-slim text-[#737C8C] text-sm mx-1 flex-shrink-0"></i>
                          <div className="flex-1 border-b border-dashed border-[#9CA3AF] mx-1 min-w-[8px]"></div>
                          <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#C5CAD4] rounded-full flex-shrink-0"></div>
                        </div>
                        <p className="text-[#6B7280] text-[11px] sm:text-[10px] text-center mt-0.5 whitespace-nowrap">
                          {flight?.elptim && (
                            <span>
                              {flight.elptim.includes(":")
                                ? `${flight.elptim.split(":")[0]}h ${flight.elptim.split(":")[1]}m`
                                : `${parseInt(flight.elptim.slice(0, -2))}h ${parseInt(
                                    flight.elptim.slice(-2),
                                  )}m`}
                            </span>
                          )}
                        </p>
                      </div>

                      <div className="flex flex-col items-end gap-0.5 w-[54px] sm:w-[88px] flex-shrink-0">
                        <p className="text-[#1F2937] text-base font-bold">
                          {flight.destinationCode}
                        </p>
                        <div className="flex items-start gap-1">
                          <i className="st-bottom-right text-[#6B7280] text-[9px] leading-none flex items-center mt-[1px]"></i>
                          <p className="text-[#6B7280] text-[11px] leading-none">
                            {flight?.arrival
                              ? new Date(flight.arrival).toLocaleTimeString(
                                  "en-GB",
                                  {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: false,
                                    timeZone: "UTC",
                                  },
                                )
                              : ""}
                            {flight?.arrival &&
                              getAirportOffsetLabel(
                                flight?.destinationCode,
                                flight.arrival,
                              ) && (
                                <span className="text-[#9CA3AF] hidden sm:inline ml-1 whitespace-nowrap">
                                  (
                                  {getAirportOffsetLabel(
                                    flight?.destinationCode,
                                    flight.arrival,
                                  )}
                                  )
                                </span>
                              )}
                          </p>
                        </div>
                      </div>

                    </div>

                      {/* Action icons area. On phones (<sm) these are pulled
                          out of the route row to the card's top-right corner so
                          the from/to/line get the full width; from sm up they sit
                          inline at the end of the row in pin / favourite / options
                          order to match the design, as plain icons (no boxes). */}
                      <div className="max-sm:absolute max-sm:top-2 max-sm:right-2 flex items-center justify-end gap-2 sm:gap-4 flex-shrink-0 min-w-[104px] sm:min-w-[136px]">
                      {/* Pin Icon — always reserves its slot (invisible when
                          unpinned) so the route section's width stays constant
                          across rows and the destination codes stay aligned. */}
                      {flight.isPinned ? (
                        <div
                          className="cursor-pointer flex items-center justify-center w-[29px] h-[29px] sm:w-8 sm:h-8 rounded-md border border-[#ECEEF1] bg-white hover:bg-gray-50 transition-colors flex-shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          <i className="st-thumbpin text-[#FFBC00] text-[16px] sm:text-[17px]"></i>
                        </div>
                      ) : (
                        <div className="w-[29px] h-[29px] sm:w-8 sm:h-8 flex-shrink-0" aria-hidden="true" />
                      )}

                      {/* Favourite Icon */}
                      <div
                        className="cursor-pointer flex items-center justify-center w-[29px] h-[29px] sm:w-8 sm:h-8 rounded-md border border-[#ECEEF1] bg-white hover:bg-gray-50 transition-colors flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isFavouritesPage) {
                            handleRemoveFavourite(flight);
                          } else {
                            toggleFavourite(flight);
                          }
                        }}
                      >
                        {isFavouritesPage || flight?.isFavourite ? (
                          // Filled Heart SVG
                          <svg
                            width="20"
                            height="19"
                            viewBox="0 0 18 17"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-[18px] h-[17px] sm:w-5 sm:h-[19px] text-red-500"
                          >
                            <path
                              d="M12.3799 0.149414C14.8323 0.149494 16.76 2.1358 17.0117 4.87012L17.0322 5.06055C17.0509 5.34293 17.0462 5.8953 16.8623 6.67773C16.5199 8.15991 15.703 9.52545 14.5283 10.5869L14.5273 10.5879L8.64844 15.834L8.54785 15.9238L8.44824 15.833L2.66211 10.5869C1.48739 9.52538 0.670474 8.15988 0.328125 6.67773C0.0876614 5.67947 0.152851 5.03447 0.176758 4.88086C0.423822 2.1401 2.37313 0.149415 4.82715 0.149414C6.34973 0.149414 7.71113 0.918999 8.56641 2.21582C9.41377 0.949865 10.8432 0.149414 12.3799 0.149414Z"
                              fill="#FFBC00"
                              stroke="#FFBC00"
                              strokeWidth="0.3"
                            />
                          </svg>
                        ) : (
                          // Outline Heart SVG
                          <svg
                            width="20"
                            height="19"
                            viewBox="0 0 18 17"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-[18px] h-[17px] sm:w-5 sm:h-[19px] text-lightgray-150"
                          >
                            <path
                              d="M12.3799 0.149414C14.8323 0.149494 16.76 2.1358 17.0117 4.87012L17.0322 5.06055C17.0509 5.34293 17.0462 5.8953 16.8623 6.67773C16.5199 8.15991 15.703 9.52545 14.5283 10.5869L14.5273 10.5879L8.64844 15.834L8.54785 15.9238L8.44824 15.833L2.66211 10.5869C1.48739 9.52538 0.670474 8.15988 0.328125 6.67773C0.0876615 5.67947 0.152851 5.03447 0.176758 4.88086C0.423822 2.1401 2.37313 0.149415 4.82715 0.149414C6.34973 0.149414 7.71113 0.918999 8.56641 2.21582C9.41377 0.949865 10.8432 0.149414 12.3799 0.149414ZM12.3809 1.06348C10.9 1.06348 9.57619 1.97344 8.98438 3.37207L8.9834 3.37305L8.68555 4.06152L8.54785 4.37891L8.41016 4.06152L8.1123 3.37305V3.37207C7.50155 1.93423 6.23117 1.0636 4.80859 1.06348C2.87685 1.06348 1.30723 2.69661 1.10742 4.96387V5L1.10547 5.0127C1.1054 5.01314 1.10469 5.01427 1.10449 5.01562C1.10404 5.01865 1.10343 5.02412 1.10254 5.03125C1.10076 5.04551 1.09896 5.06832 1.09668 5.09863C1.09212 5.15934 1.08748 5.2517 1.09082 5.37402C1.09585 5.55777 1.1175 5.80941 1.17773 6.12305L1.25195 6.45801L1.25293 6.45898H1.25195C1.5607 7.74926 2.2704 8.93277 3.30957 9.88086L8.54883 14.6455L13.8984 9.88086C14.9366 8.95151 15.6453 7.76836 15.9541 6.45996C16.0634 5.98697 16.0951 5.6208 16.0996 5.37305C16.1019 5.24919 16.0977 5.1546 16.0928 5.09082C16.0903 5.05896 16.0879 5.03443 16.0859 5.01758C16.085 5.00905 16.0837 5.00262 16.083 4.99707C16.0827 4.99456 16.0823 4.99102 16.082 4.98828V4.98242L16.0586 4.76855C15.7837 2.57934 14.2521 1.0637 12.3809 1.06348Z"
                              fill="currentColor"
                              stroke="currentColor"
                              strokeWidth="0.3"
                            />
                          </svg>
                        )}
                      </div>

                      {!isFavouritesPage && (
                        <div className="relative flex-shrink-0">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown(flight.id);
                            }}
                            className="flex items-center justify-center w-[29px] h-[29px] sm:w-8 sm:h-8 rounded-md border border-[#ECEEF1] bg-white hover:bg-gray-50 transition-colors"
                          >
                            <i className="st-more text-[13px] sm:text-sm text-[#6B7280] rotate-90"></i>
                          </button>

                          {/* Conditional rendering of dropdown */}
                          {openDropdown === flight.id && (
                            <div
                              ref={dropdownRef}
                              className="absolute z-10 bg-white divide-y divide-gray-100 rounded-lg w-60 shadow-2xl py-2 border"
                              style={
                                dropdownPosition === "up"
                                  ? { bottom: "100%", right: "0" }
                                  : { top: "100%", right: "0" }
                              }
                            >
                              <ul className="px-4 text-md text-gray-700">
                                <li>
                                  <a
                                    href="#"
                                    className="flex items-center gap-2 py-2 px-2 mt-2 rounded-md hover:bg-gray-50 transition-colors"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handlePinUnPin(flight);
                                    }}
                                  >
                                    <i className="st-thumbpin text-gray-600 text-md"></i>
                                    <p className="text-gray-800 text-md">
                                      {flight?.isPinned
                                        ? "Unpin Flight"
                                        : "Pin Flight"}
                                    </p>
                                  </a>
                                </li>
                                {canRequestUpdate(flight.status) && (
                                  <li>
                                    <a
                                      href="#"
                                      className="flex items-center gap-2 py-2 px-2 mt-2 rounded-md hover:bg-gray-50 transition-colors"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleSingleUpdate(flight);
                                      }}
                                    >
                                      <i className="st-reload text-gray-600 text-md"></i>
                                      <p className="text-gray-800 text-md">
                                        Request Update
                                      </p>
                                    </a>
                                  </li>
                                )}
                                {/* Hide once the load has already been reported
                                    (isInaccurateResponse === true) — no point
                                    reporting the same answer twice. */}
                                {canReportInaccurate(flight.status) &&
                                  !flight.isInaccurateResponse && (
                                    <li>
                                      <a
                                        href="#"
                                        className="flex items-center gap-2 py-2 px-2 mt-2 rounded-md hover:bg-gray-50 transition-colors"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setSelectedFlightId(flight.responseId);
                                          setIsReportModalOpen(true);
                                        }}
                                      >
                                        <i className="st-enable text-gray-600 text-md"></i>
                                        <p className="text-gray-800 text-md">
                                          Report Inaccurate Load
                                        </p>
                                      </a>
                                    </li>
                                  )}
                                {flight.status === "Pending" && (
                                  <li>
                                    <a
                                      href="#"
                                      className="flex items-center gap-2 py-2 px-2 mt-2 rounded-md hover:bg-gray-50 transition-colors"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();

                                        if (!flight) return;
                                        const {
                                          airlineName,
                                          arrival,
                                          departure,
                                          sourceCode,
                                          destinationCode,
                                          flightNo,
                                          id,
                                          requestDetailId,
                                          airlineId,
                                        } = flight;
                                        const baseUrl =
                                          process.env
                                            .REACT_APP_PUBLIC_BASE_URL ||
                                          window.location.origin;
                                        const url = `${baseUrl}/share-request?airlineName=${encodeURIComponent(airlineName)}&arrival=${encodeURIComponent(arrival)}&departure=${encodeURIComponent(departure)}&sourceCode=${encodeURIComponent(sourceCode)}&destinationCode=${encodeURIComponent(destinationCode)}&flightNo=${encodeURIComponent(flightNo)}&id=${encodeURIComponent(id)}&requestDetailId=${encodeURIComponent(requestDetailId)}&airlineId=${encodeURIComponent(airlineId)}`;
                                        setShareUrl(url);
                                        setShareRequestModalOpen(true);
                                      }}
                                    >
                                      <i className="st-share text-gray-600 text-md"></i>
                                      <p className="text-gray-800 text-md">
                                        Share Request
                                      </p>
                                    </a>
                                  </li>
                                )}
                                {flight.isEnabledStatus === false &&
                                  hasAnswerDetails(flight.status) && (
                                    <li>
                                      <a
                                        href="#"
                                        className="flex items-center gap-2 py-2 px-2 mt-2 rounded-md hover:bg-gray-50 transition-colors"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handleSingleStatusUpdate(flight);
                                        }}
                                      >
                                        <i className="st-warning text-gray-600 text-md"></i>
                                        <p className="text-gray-800 text-md">
                                          Enable Status Update
                                        </p>
                                      </a>
                                    </li>
                                  )}
                                <li>
                                  <a
                                    href="#"
                                    className="flex items-center gap-2 py-2 px-2 mt-2 rounded-md hover:bg-gray-50 transition-colors"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleSingleDelete(flight);
                                    }}
                                  >
                                    <i className="st-delete text-gray-600 text-md"></i>
                                    <p className="text-gray-800 text-md">
                                      Delete Request
                                    </p>
                                  </a>
                                </li>
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                      </div>
                  </div>
                </div>
              </div>

              {/* 
            
            {openAccordion === flight?.requestDetailId && flight?.status !== 'Answered' && (
              <div className="bg-white rounded-b-lg mt-3">
                <div className="p-5 border-t border-gray-300">
                  <div className="flex items-center p-1.5 bg-indigo-200 rounded-full w-fit">
                    <div
                      className={`px-3 py-1 rounded-full text-sm font-medium cursor-pointer ${!isArrival ? "bg-white text-royalblue-100" : "text-gray-500"
                        }`}
                      onClick={() => setIsArrival(false)}
                    >
                      Departure
                    </div>
                    <div
                      className={`px-3 py-1 rounded-full text-sm font-medium cursor-pointer ${isArrival ? "bg-white text-royalblue-100" : "text-gray-500"
                        }`}
                      onClick={() => setIsArrival(true)}
                    >
                      Arrival
                    </div>
                  </div>

                  <div className="grid grid-cols-12 gap-6 items-center justify-between mt-6">
                    <div className="flex items-center lg:col-span-2 md:col-span-8 col-span-12 gap-3">
                      <div className="flex flex-col gap-1">
                        <p className="text-grease-100 text-sm font-medium">
                          {flightData?.sourceCode}
                        </p>
                        <p className="text-lightgray-200 mt-1 text-xs">
                       
                        </p>
                      </div>
                      <div className="w-full">
                       
                        <div className="flex items-center w-full">
                          <div className="w-3 h-3 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <div className="w-2 h-2 bg-white rounded-full flex items-center justify-center">
                              <div className="w-1 h-1 bg-royalblue-100 rounded-full"></div>
                            </div>
                          </div>
                          <div className="w-full border-b-2 border-dashed border-indigo-200"></div>
                          <div className="w-3 h-3 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <div className="w-2 h-2 bg-white rounded-full flex items-center justify-center">
                              <div className="w-1 h-1 bg-royalblue-100 rounded-full"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex flex-col items-end gap-1">
                        <p className="text-grease-100 text-sm font-medium">
                          {flightData?.destinationCode}
                        </p>
                        <p className="text-lightgray-200 mt-1 text-xs">
                           {flight.to.city} 
                        </p>
                      </div>
                    </div>

                  
                    <div className="flex items-center lg:justify-normal md:justify-end lg:col-start-4 lg:col-span-4 md:col-span-4 col-span-12 gap-6">
                      <div>
                        <p className="text-lightgray-200 text-xs">
                          {isArrival ? "Arrival" : "Departure"}
                        </p>
                        <p className="text-grease-100 mt-2 text-sm font-medium">
                          <p className="text-grease-100 mt-2 text-sm font-medium">
                            {new Date(isArrival ? flightData?.arrival ?? "" : flightData?.departure ?? "").toLocaleString("en-US", {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                              timeZone: "UTC",
                            })}
                          </p>

                        </p>
                      </div>
                      <div>
                       
                        <p className="text-lightgray-200 mt-2 text-xs">
                         {flight.timeLeft} 
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center lg:col-span-5 md:col-span-12 col-span-12 gap-3">
                     
                      <div>
                        <p className="text-lightgray-200 text-xs">Terminal</p>
                        <p className="text-grease-100 mt-2 text-sm font-medium">
                          {isArrival ? flightData?.arrivalTerminal || 1 : flightData?.departureTerminal || 0}
                        </p>
                      </div>
                    
                      <div>
                        <p className="text-lightgray-200 text-xs">Aircraft</p>
                        <p className="text-grease-100 mt-2 text-sm font-medium">
                          {flightData?.flightNo}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )} 
               */}

              {/* Add this new condition to show the "Answered" accordion */}
              {openAccordion.has(flight.id) &&
                hasAnswerDetails(flight?.status) && (
                  <AnsweredFlightDetails
                    flight={flight}
                    isUpdatedAnswer={isUpdatedAnswer(flight?.status)}
                  />
                )}
            </div>
          </div>
        ))
      )}

      {/* Favourites page only: pagination renders inline under the list,
          matching the Notification page layout. */}
      {isFavouritesPage && localFlights.length > 0 && footer}

      {isReportModalOpen && (
        <ReportInaccurateLoadPopup
          loadResponseId={selectedFlightId ?? 0}
          onClose={() => setIsReportModalOpen(false)}
        />
      )}

      {/* Request Update Modal - Now handling both single and bulk operations */}
      <RequestUpdateModal
        isOpen={isRequestUpdateOpen}
        onClose={() => {
          setIsRequestUpdateOpen(false);
          setOpenDropdown(null);
        }}
        flightNo={currentFlight?.flightNo}
        createdDate={currentFlight?.createdDate}
        lastUpdatedDate={currentFlight?.answeredDate ?? currentFlight?.modifiedDate}
        isBulkOperation={isBulkOperation}
        requestIds={
          isBulkOperation
            ? getSelectedRequestIds()
            : currentFlight
              ? [currentFlight?.id]
              : []
        }
        costPerRequest={requestCost ?? 1}
        onConfirm={handleConfirmRequestUpdate}
      />

      <DeleteRequestModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setOpenDropdown(null);
        }}
        flightNo={currentFlight?.flightNo}
        createdDate={currentFlight?.createdDate}
        departureDate={currentFlight?.departure}
        isBulkOperation={isBulkOperation}
        requestIds={
          isBulkOperation
            ? Object.values(selectedFlightDetails)
                .map((flight: any) => flight.requestDetailId)
                .filter(Boolean)
            : currentFlight
              ? [currentFlight?.requestDetailId]
              : []
        }
        onConfirm={handleConfirmDelete}
      />

      {/* Share Request Modal */}
      <ShareRequestModal
        isOpen={isShareRequestModalOpen}
        onClose={() => setShareRequestModalOpen(false)}
        shareUrl={shareUrl}
      />

      <AirlineNotesModal
        isOpen={isNotesModalOpen}
        onClose={handleCloseNotes}
        airlineNote={selectedAirlineNote}
      />

      {/* Favourites page only: fixed bottom bar holding the POST REQUEST
          button, pinned to the viewport while the list scrolls. Pagination
          is rendered inline under the list instead (see above). */}
      {isFavouritesPage && selectedFlights.length > 0 && (
        <>
          {/* Reserve scroll room so the last rows aren't hidden behind the
              fixed bar. */}
          <div aria-hidden className="h-8 sm:h-24" />
          <div className="fixed inset-x-0 bottom-0 md:left-[220px] z-40 bg-white/95 backdrop-blur border-t border-[#EDEEF1]">
            <div className="flex flex-col items-center gap-2 px-4 py-3">
              <button
                type="button"
                className="p-3 sm:p-4 w-full max-w-sm rounded-lg text-[#192227] text-sm font-semibold transition-all bg-[#FFBC00] hover:bg-[#F59E0B] shadow-button hover:scale-105"
                onClick={openPostModal}
              >
                POST REQUEST
              </button>
            </div>
          </div>
        </>
      )}

      {isFavouritesPage && isPostModalOpen && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="bg-white rounded-xl p-6 shadow-2xl w-full max-w-md mx-4"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <h3 className="text-lg font-semibold">Post Request</h3>
              <button
                aria-label="Close"
                onClick={() => setIsPostModalOpen(false)}
                className="text-lightgray-150"
              >
                ✕
              </button>
            </div>
            <div className="my-4">
              <label className="text-xs text-lightgray-200 block mb-2">
                Date
              </label>
              <div className="relative">
                <DatePicker
                  selected={postDate ? new Date(postDate) : null}
                  onChange={(date: Date | null) =>
                    setPostDate(date ? date.toISOString().slice(0, 10) : "")
                  }
                  minDate={new Date()}
                  className="w-full p-2 border border-gray-200 rounded"
                  wrapperClassName="w-full"
                  popperClassName="st-favourites-datepicker"
                  placeholderText="Select departure date"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setIsPostModalOpen(false)}
                className="px-4 py-2 bg-gray-200 rounded text-sm"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  // Selection can span pages, so derive ids directly rather
                  // than looking them up in localFlights (current page only).
                  const selectedFavIds = selectedFlights
                    .map((id) => Number(id))
                    .filter((id): id is number => !isNaN(id));
                  try {
                    setPostRequestLoader(true);
                    if (selectedFavIds.length > 0) {
                      await Promise.all(
                        selectedFavIds.map((fid) =>
                          validateFavouriteFlight(fid, postDate),
                        ),
                      );
                      useToastStore
                        .getState()
                        .showToast(
                          "Flight request posted successfully",
                          "success",
                          3000,
                        );
                    }
                  } catch (error: any) {
                    const errorMessage =
                      error?.response?.data?.message ||
                      error?.message ||
                      "Favourite validation failed";
                    useToastStore
                      .getState()
                      .showToast(errorMessage, "error", 4000);
                  } finally {
                    // Always reset the UI after a post attempt: close the modal
                    // and clear the row selection so a failed/stale selection
                    // (e.g. an already-departed flight) doesn't persist.
                    setPostRequestLoader(false);
                    setIsPostModalOpen(false);
                    setSelectedFlights([]);
                  }
                }}
                className="px-4 py-2 bg-[#FFBC00] hover:bg-[#F59E0B] rounded text-sm text-[#192227] font-semibold"
                disabled={selectedFlights.length === 0}
              >
                {postRequestLoader ? "Confirming..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default FlightListing;
